import { captureException } from '@sentry/react';
import { GenericError } from 'app/common/components/errors';
import type { trackEvent } from 'app/user-tracking/actions';
import type React from 'react';
import { Component } from 'react';
import { context } from '../../config';

export type ErrorBoundaryProps = {
  readonly children?: React.ReactNode;
  readonly pathname: string;
  readonly trackEvent: typeof trackEvent;
  readonly ErrorComponent?: React.ComponentType;
};

export type ErrorBoundaryState = {
  hasError: boolean;
  pathname: string;
};

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  static getDerivedStateFromProps(props: ErrorBoundaryProps, state: ErrorBoundaryState): ErrorBoundaryState | null {
    if (props.pathname !== state.pathname) {
      return { pathname: props.pathname, hasError: false };
    }

    return null;
  }

  static getDerivedStateFromError() {
    // Update state to render fallback UI
    return { hasError: true };
  }

  public override state: ErrorBoundaryState = {
    hasError: false,
    pathname: this.props.pathname
  };

  public override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error in Sentry
    if (context.tier === 'dev') {
      console.error(error);
    }

    const { componentStack } = errorInfo;
    captureException(error, { contexts: { react: { componentStack } } });
    this.props.trackEvent({
      eventName: 'Javascript Error',
      data: { error, info: errorInfo }
    });
  }

  override render() {
    const { ErrorComponent = GenericError } = this.props;
    if (this.state.hasError) {
      return <ErrorComponent />;
    }

    return this.props.children;
  }
}
